import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAudioPlayback } from "@/modules/common/hooks/audio";
import { RecordingEntry } from "@/modules/common/types/audio.types";
import {
  generateFallbackWaveform,
  generateWaveformFromUrl,
  WAVEFORM_BAR_COUNT,
  WaveformResult,
} from "@/modules/common/utils/audio.utils";
import { WhatsappMessage } from "@/modules/lead-page/dtos/index-v2";

export interface WhatsappAudioPlaybackContext {
  getAudioId: (msg: WhatsappMessage) => string;
  isPlaying: (id: string) => boolean;
  getCurrentTimeForId: (id: string) => number;
  togglePlay: (id: string, url: string) => void;
  audioLoading: boolean;
  playingId: string | null;
  loadedId: string | null;
  getErrorForId: (id: string) => string | null;
  seekTo: (id: string, time: number) => void;
  playbackRate: number;
  setPlaybackRate: (rate: number) => void;
  toRecordingEntry: (msg: WhatsappMessage) => RecordingEntry;
}

/* Messages are numbered per page (1..n), so ids repeat once a second page is
   loaded — pairing the id with the timestamp keeps playback, waveform and
   error state bound to the right bubble in a paginated thread. */
const getAudioId = (msg: WhatsappMessage) => `${msg.id}-${msg.time}`;

/**
 * Voice-note playback for a WhatsApp message thread: a single audio element
 * shared by every bubble plus lazily generated per-message waveforms. Shared by
 * the activity conversation drawer and the Chats & Calls thread panel so both
 * render voice notes the same way.
 */
export const useWhatsappAudioPlayback = (
  messages: WhatsappMessage[],
): WhatsappAudioPlaybackContext => {
  const {
    togglePlay,
    isPlaying,
    getCurrentTimeForId,
    getDurationForId,
    isLoading: audioLoading,
    getErrorForId,
    playingId,
    loadedId,
    seekTo,
    playbackRate,
    setPlaybackRate,
  } = useAudioPlayback();

  const [audioMetadataMap, setAudioMetadataMap] = useState<
    Record<string, WaveformResult>
  >({});
  /* Tracks waveforms already requested — the metadata map isn't readable from
     inside the effect without re-triggering it on every generated waveform. */
  const requestedWaveformsRef = useRef<Set<string>>(new Set());

  const audioMessages = useMemo(
    () =>
      messages.filter((msg) => msg.messageType === "audioMessage" && msg.mediaUrl),
    [messages],
  );

  useEffect(() => {
    if (audioMessages.length === 0) return;
    let cancelled = false;
    const loadWaveforms = async () => {
      for (const msg of audioMessages) {
        const id = getAudioId(msg);
        if (!msg.mediaUrl || requestedWaveformsRef.current.has(id)) continue;
        requestedWaveformsRef.current.add(id);
        const result = await generateWaveformFromUrl(
          msg.mediaUrl,
          WAVEFORM_BAR_COUNT,
        );
        if (cancelled) return;
        setAudioMetadataMap((prev) => ({ ...prev, [id]: result }));
      }
    };
    loadWaveforms();
    return () => {
      cancelled = true;
    };
  }, [audioMessages]);

  const toRecordingEntry = useCallback(
    (msg: WhatsappMessage): RecordingEntry => {
      const id = getAudioId(msg);
      const localMetadata = audioMetadataMap[id];
      const playbackDuration = getDurationForId(id);
      const duration =
        playbackDuration > 0 ? playbackDuration : localMetadata?.duration || 0;
      return {
        id,
        audioBlob: null,
        audioUrl: msg.mediaUrl ?? undefined,
        duration: duration > 0 ? duration : 0,
        waveformData:
          localMetadata?.waveformData ||
          generateFallbackWaveform(WAVEFORM_BAR_COUNT),
      };
    },
    [audioMetadataMap, getDurationForId],
  );

  return useMemo<WhatsappAudioPlaybackContext>(
    () => ({
      getAudioId,
      isPlaying,
      getCurrentTimeForId,
      togglePlay,
      audioLoading,
      playingId,
      loadedId,
      getErrorForId,
      seekTo,
      playbackRate,
      setPlaybackRate,
      toRecordingEntry,
    }),
    [
      isPlaying,
      getCurrentTimeForId,
      togglePlay,
      audioLoading,
      playingId,
      loadedId,
      getErrorForId,
      seekTo,
      playbackRate,
      setPlaybackRate,
      toRecordingEntry,
    ],
  );
};
